import axios from 'axios';
import { API_BASE_URL } from '../../config';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const formatCurrency = (value) => {
    const numericValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(numericValue);
};

const formatDecimal = (value, decimals = 2) => {
    const numericValue = parseFloat(value) || 0;
    return numericValue.toFixed(decimals).replace('.', ',');
}

const formatDate = (dateInput) => {
    if (!dateInput) return '-';

    try {
        let dateStr = String(dateInput);
        
        if (dateStr.includes('T')) {
            dateStr = dateStr.split('T')[0];
        } else if (dateStr.includes(' ')) {
            dateStr = dateStr.split(' ')[0];
        }

        if (dateStr.includes('-')) {
            const partes = dateStr.split('-');
            if (partes[0].length === 4) {
                const [ano, mes, dia] = partes;
                return `${dia}/${mes}/${ano}`;
            }
        }

        const d = new Date(dateInput);
        if (!isNaN(d.getTime())) {
            const dia = String(d.getDate()).padStart(2, '0');
            const mes = String(d.getMonth() + 1).padStart(2, '0');
            const ano = d.getFullYear();
            return `${dia}/${mes}/${ano}`;
        }

        return dateStr;
    } catch (error) {
        return String(dateInput);
    }
};

const GerarRelatorioPDF = async ({ 
    equipeId, 
    nomeEquipe, 
    filtroInicio, 
    filtroFim, 
    filtroMaquinaId, 
    veiculoFiltrado,
    chartImageBase64 
}) => {
    const token = localStorage.getItem('token');
    
    if (!token) {
        throw new Error("Sessão expirada. Faça login novamente.");
    }
    
    try {
        const params = {
            dataInicio: filtroInicio,
            dataFim: filtroFim,
            veiculoId: filtroMaquinaId || ''
        };

        const response = await axios.get(`${API_BASE_URL}/api/producao/equipes/${equipeId}/detalhes`, {
            headers: { Authorization: `Bearer ${token}` },
            params: params
        });

        const detalhes = response.data;
        
        if (detalhes.length === 0) {
            throw new Error("Nenhum registro de produção encontrado.");
        }

        const normalize = (text) => (text || "").toUpperCase().replace(/\s/g, '').replace(/[^A-Z0-9]/g, '');

        const dadosCarga = detalhes.filter(d => {
            const fCol = normalize(d.tipo_colhe_ta);
            const mVei = normalize(d.veiculo_modelo);
            const ident = normalize(d.veiculo_identificacao);
            
            return fCol.includes("FORWARDER") || 
                   ident.includes("FORWARDER") || 
                   mVei.includes("FORWARDER") ||
                   mVei.includes("292ADVANCED") || 
                   mVei.includes("192ADVANCED") ||
                   ident.includes("FORWARDER") ||
                   ident.includes("192ADV") ||
                   ident.includes("292ADV");
        });

        const dadosProducao = detalhes.filter(d => {
            const fCol = normalize(d.tipo_colheita);
            const mVei = normalize(d.veiculo_modelo);
            const ident = normalize(d.veiculo_identificacao);
            
            return !(fCol.includes("FORWARDER") || 
                     ident.includes("FORWARDER") || 
                     mVei.includes("FORWARDER") ||
                     mVei.includes("292ADVANCED") || 
                     mVei.includes("192ADVANCED") ||
                     ident.includes("FORWARDER") ||
                     ident.includes("192ADV") ||
                     ident.includes("292ADV"));
        });

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        let y = 20;

        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(22, 163, 74);
        doc.text(`RELATÓRIO DE DESEMPENHO OPERACIONAL`, 15, y); 
        y += 8;
        
        doc.setLineWidth(0.8);
        doc.setDrawColor(22, 163, 74); 
        doc.line(15, y, pageWidth - 15, y); 
        y += 8;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Equipe: ${nomeEquipe}`, 15, y);
        doc.text(`Período: ${formatDate(filtroInicio)} a ${formatDate(filtroFim)}`, 15, y + 5);
        doc.text(`Máquina Filtrada: ${veiculoFiltrado}`, 15, y + 10);
        y += 20;

        if (dadosCarga.length > 0) {
            let tHoras = 0, tProd = 0, tParadas = 0;
            const diasU = new Set();

            dadosCarga.forEach(d => {
                tHoras += Math.max(0, (parseFloat(d.horimetro_final) || 0) - (parseFloat(d.horimetro_inicial) || 0));
                tProd += parseFloat(d.producao_diaria_valor) || 0;
                tParadas += parseFloat(d.horas_paradas) || 0;
                if(d.data) diasU.add(String(d.data).split('T')[0]);
            });

            const nDias = diasU.size;
            
            doc.setFontSize(13).setFont(undefined, 'bold').text("RESUMO: MÁQUINAS DE CARGA (FORWARDER / ADVANCED)", 15, y);
            y += 6;

            const kpiData = [
                ["Total de Cargas", `${formatDecimal(tProd, 2)} cargas`],
                ["Total Horas Trabalhadas", `${formatDecimal(tHoras, 2)} h`],
                ["Cargas / dia", `${formatDecimal(nDias > 0 ? tProd/nDias : 0, 2)} cargas/dia`],
                ["Cargas por hora", `${formatDecimal(tHoras > 0 ? tProd/tHoras : 0, 2)} cargas/h`],
                ["Total Horas Paradas", `${formatDecimal(tParadas, 2)} h`],
                ["% Tempo Parado", `${formatDecimal((tHoras + tParadas > 0 ? (tParadas/(tHoras + tParadas)*100) : 0), 2)} %`]
            ];

            doc.autoTable({
                head: [[{ content: "Métrica", styles: { fillColor: [22, 163, 74] } }, { content: "Valor", styles: { fillColor: [22, 163, 74], halign: 'right' } }]],
                body: kpiData,
                startY: y,
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 2 },
                columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
                margin: { left: 15 },
                tableWidth: 100
            });

            y = doc.autoTable.previous.finalY + 10;

            doc.autoTable({
                head: [["Data", "Máquina", "Operador", "Horas", "Cargas", "Local da Colheita", "Parada", "Talhão", "Nota"]],
                body: dadosCarga.map(d => [
                    formatDate(d.data), d.veiculo_modelo, d.operador_username,
                    (Math.max(0, (parseFloat(d.horimetro_final) || 0) - (parseFloat(d.horimetro_inicial) || 0))).toFixed(2),
                    (parseFloat(d.producao_diaria_valor) || 0).toFixed(2),
                    d.local_colheita || '-',
                    d.horas_paradas ? `${d.horas_paradas}h` : '0h',
                    d.talhao_colheita || '-',
                    d.nota_relevante || '-'
                ]),
                startY: y,
                theme: 'striped',
                styles: { fontSize: 8.5 },
                headStyles: { fillColor: [22, 163, 74] },
                margin: { left: 15, right: 15 }
            });

            y = doc.autoTable.previous.finalY + 15;
        }

        if (dadosProducao.length > 0) {
            if (y > 150) { doc.addPage(); y = 20; }
            
            let tHoras = 0, tProd = 0, tParadas = 0;
            const diasU = new Set();

            dadosProducao.forEach(d => {
                tHoras += Math.max(0, (parseFloat(d.horimetro_final) || 0) - (parseFloat(d.horimetro_inicial) || 0));
                tProd += parseFloat(d.producao_diaria_valor) || 0;
                tParadas += parseFloat(d.horas_paradas) || 0;
                if(d.data) diasU.add(String(d.data).split('T')[0]);
            });

            const nDias = diasU.size;

            doc.setFontSize(13).setFont(undefined, 'bold').text("RESUMO: MÁQUINAS DE PRODUÇÃO (m³)", 15, y);
            y += 6;

            const kpiData = [
                ["Total de Produção", `${formatDecimal(tProd, 2)} m³`],
                ["Total Horas Trabalhadas", `${formatDecimal(tHoras, 2)} h`],
                ["Média Diária", `${formatDecimal(nDias > 0 ? tProd/nDias : 0, 2)} m³/dia`],
                ["Média Horária", `${formatDecimal(tHoras > 0 ? tProd/tHoras : 0, 2)} m³/h`],
                ["Total Horas Paradas", `${formatDecimal(tParadas, 2)} h`],
                ["% Tempo Parado", `${formatDecimal((tHoras + tParadas > 0 ? (tParadas/(tHoras + tParadas)*100) : 0), 2)} %`]
            ];

            doc.autoTable({
                head: [[{ content: "Métrica", styles: { fillColor: [59, 130, 246] } }, { content: "Valor", styles: { fillColor: [59, 130, 246], halign: 'right' } }]],
                body: kpiData,
                startY: y,
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 2 },
                columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
                margin: { left: 15 },
                tableWidth: 100
            });

            y = doc.autoTable.previous.finalY + 10;

            doc.autoTable({
                head: [["Data", "Máquina", "Operador", "Horas", "Produção", "Local da Colheita", "Parada","Talhão", "Nota" ]],
                body: dadosProducao.map(d => [
                    formatDate(d.data), d.veiculo_modelo, d.operador_username,
                    (Math.max(0, (parseFloat(d.horimetro_final) || 0) - (parseFloat(d.horimetro_inicial) || 0))).toFixed(2),
                    (parseFloat(d.producao_diaria_valor) || 0).toFixed(2),
                    d.local_colheita || '-',
                    d.horas_paradas ? `${d.horas_paradas}h` : '0h',
                    d.talhao_colheita || '-', 
                    d.nota_relevante || '-'
                ]),
                startY: y,
                theme: 'striped',
                styles: { fontSize: 8.5 },
                headStyles: { fillColor: [59, 130, 246] },
                margin: { left: 15, right: 15 }
            });

            y = doc.autoTable.previous.finalY + 15;
        }

        if (chartImageBase64) {
            doc.addPage();
            y = 20;

            const chartWidth = 140;
            const chartHeight = 120;
            const centerX = (pageWidth - chartWidth) / 2;

            doc.setFillColor(248, 250, 252);
            doc.roundedRect(10, y, pageWidth - 20, chartHeight + 30, 3, 3, 'F');
            
            doc.setFontSize(14).setFont(undefined, 'bold').setTextColor(71, 85, 105);
            doc.text("DISTRIBUIÇÃO DE HORAS GERAL", pageWidth / 2, y + 12, { align: 'center' });
            
            doc.addImage(chartImageBase64, 'PNG', centerX, y + 20, chartWidth, chartHeight, undefined, 'SLOW');
        }

        doc.save(`Relatorio_Profissional_${nomeEquipe}.pdf`);

    } catch (error) {
        throw new Error(`Erro: ${error.message}`);
    }
};

export default GerarRelatorioPDF;